import type { Project } from '@bigint-project/core';

import {
  DEFAULT_COLUMNS,
  cloneDefaultReportPresets,
  sanitizeReportPresets,
  type ColumnDef,
  type ReportPreset,
  type ScheduleMode,
  type TimeScale,
  type ViewName,
} from '../store/types';

/**
 * `.bproj` 專案檔格式。
 *
 * 選型：單一 JSON 檔（而非 SQLite）。理由見 `apps/desktop/README.md`
 * ——這裡只放與序列化直接相關的重點：
 * - `Project`（見 `packages/core/src/domain/project.ts`）已經是純資料
 *   （branded 型別在執行期就是 number/string），JSON round-trip 零損失。
 * - `formatVersion` 讓日後遷移（例如改存 SQLite）有明確的分岔點。
 * - 任何這版程式「不認識」的欄位（頂層或 `view` 底下）在讀檔時會被保留
 *   （`unknownTopLevelFields`/`unknownViewFields`），存檔時原樣寫回。
 *   沒有這一步，舊版程式打開一個新版程式寫入過額外欄位的檔案、再存一次，
 *   就會把那些欄位永久洗掉——這正是加 `formatVersion` 想避免的資料遺失。
 */
export const BPROJ_FORMAT_VERSION = 1 as const;

export interface BprojViewSnapshot {
  readonly activeView: ViewName;
  readonly timeScale: TimeScale;
  readonly collapsedTaskIds: readonly string[];
  readonly columns: readonly ColumnDef[];
  readonly showBaseline: boolean;
  readonly showCriticalPath: boolean;
  readonly scheduleMode: ScheduleMode;
  /** Optional only for source compatibility; persisted snapshots are always canonicalized. */
  readonly reportPresets?: readonly ReportPreset[];
  readonly activeReportPresetId?: string | null;
}

export interface BprojFile {
  readonly formatVersion: typeof BPROJ_FORMAT_VERSION;
  readonly savedAt: string;
  readonly project: Project;
  readonly view: BprojViewSnapshot | null;
  /** 頂層未知欄位（不含 formatVersion/savedAt/project/view）。 */
  readonly unknownTopLevelFields: Readonly<Record<string, unknown>>;
  /** `view` 底下未知欄位；`view` 為 null 時一律為空物件。 */
  readonly unknownViewFields: Readonly<Record<string, unknown>>;
}

export interface SerializeExtras {
  readonly unknownTopLevelFields?: Readonly<Record<string, unknown>>;
  readonly unknownViewFields?: Readonly<Record<string, unknown>>;
}

export class BprojParseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'BprojParseError';
  }
}

const KNOWN_TOP_LEVEL_KEYS = new Set(['formatVersion', 'savedAt', 'project', 'view']);
const KNOWN_VIEW_KEYS = new Set([
  'activeView',
  'timeScale',
  'collapsedTaskIds',
  'columns',
  'showBaseline',
  'showCriticalPath',
  'scheduleMode',
  'reportPresets',
  'activeReportPresetId',
]);

const VIEW_NAMES: ReadonlySet<string> = new Set<ViewName>([
  'gantt',
  'networkDiagram',
  'resourceSheet',
  'resourceUsage',
  'costTable',
  'projectOverview',
  'evmReport',
  'reports',
  'calendar',
  'board',
  'timesheet',
]);
const TIME_SCALES: ReadonlySet<string> = new Set<TimeScale>(['day', 'week', 'month', 'quarter']);
const SCHEDULE_MODES: ReadonlySet<string> = new Set<ScheduleMode>(['auto', 'manual']);

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function extractUnknown(
  obj: Readonly<Record<string, unknown>>,
  knownKeys: ReadonlySet<string>,
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (!knownKeys.has(key)) result[key] = value;
  }
  return result;
}

function isColumnDef(value: unknown): value is ColumnDef {
  return (
    isPlainObject(value) &&
    typeof value.id === 'string' &&
    typeof value.field === 'string' &&
    typeof value.label === 'string' &&
    typeof value.width === 'number' &&
    typeof value.visible === 'boolean'
  );
}

export function serializeProject(
  project: Project,
  view: BprojViewSnapshot | null = null,
  savedAt: string = new Date().toISOString(),
  extras: SerializeExtras = {},
): string {
  // 已知欄位一律放在展開的未知欄位「之後」，確保萬一有 key 撞名，已知欄位
  // （目前程式主動維護的值）優先，不會被過時的未知欄位快照蓋掉。
  let viewPayload: (BprojViewSnapshot & Record<string, unknown>) | null = null;
  if (view !== null) {
    const reportPresets = sanitizeReportPresets(view.reportPresets) ?? cloneDefaultReportPresets();
    const activeReportPresetId =
      view.activeReportPresetId !== undefined && reportPresets.some((preset) => preset.id === view.activeReportPresetId)
        ? view.activeReportPresetId
        : reportPresets[0]?.id ?? null;
    viewPayload = { ...extras.unknownViewFields, ...view, reportPresets, activeReportPresetId };
  }

  const file = {
    ...extras.unknownTopLevelFields,
    formatVersion: BPROJ_FORMAT_VERSION,
    savedAt,
    project,
    view: viewPayload,
  };
  return JSON.stringify(file, null, 2);
}

/**
 * 逐欄驗證並補上預設值，畸形／缺漏欄位不會讓 undefined 流進 store setter。
 * 同時回傳這版程式不認識的欄位，供 `serializeProject` 存檔時原樣寫回。
 */
function parseViewSnapshot(raw: unknown): {
  readonly view: BprojViewSnapshot | null;
  readonly unknownViewFields: Readonly<Record<string, unknown>>;
} {
  if (!isPlainObject(raw)) {
    return { view: null, unknownViewFields: {} };
  }

  const activeView: ViewName = VIEW_NAMES.has(raw.activeView as string)
    ? (raw.activeView as ViewName)
    : 'gantt';
  const timeScale: TimeScale = TIME_SCALES.has(raw.timeScale as string)
    ? (raw.timeScale as TimeScale)
    : 'week';
  const collapsedTaskIds: readonly string[] = Array.isArray(raw.collapsedTaskIds)
    ? raw.collapsedTaskIds.filter((entry): entry is string => typeof entry === 'string')
    : [];
  const columns: readonly ColumnDef[] =
    Array.isArray(raw.columns) && raw.columns.length > 0 && raw.columns.every(isColumnDef)
      ? (raw.columns as readonly ColumnDef[])
      : DEFAULT_COLUMNS;
  const showBaseline = typeof raw.showBaseline === 'boolean' ? raw.showBaseline : false;
  const showCriticalPath = typeof raw.showCriticalPath === 'boolean' ? raw.showCriticalPath : false;
  const scheduleMode: ScheduleMode = SCHEDULE_MODES.has(raw.scheduleMode as string)
    ? (raw.scheduleMode as ScheduleMode)
    : 'auto';
  const reportPresets = sanitizeReportPresets(raw.reportPresets) ?? cloneDefaultReportPresets();
  const activeReportPresetId =
    typeof raw.activeReportPresetId === 'string' && reportPresets.some((preset) => preset.id === raw.activeReportPresetId)
      ? raw.activeReportPresetId
      : reportPresets[0]?.id ?? null;

  const view: BprojViewSnapshot = {
    activeView,
    timeScale,
    collapsedTaskIds,
    columns,
    showBaseline,
    showCriticalPath,
    scheduleMode,
    reportPresets,
    activeReportPresetId,
  };
  return { view, unknownViewFields: extractUnknown(raw, KNOWN_VIEW_KEYS) };
}

/**
 * 解析 `.bproj` 內容。只做結構層的存在性檢查（不重新驗證領域規則 ——
 * 那是 core 引擎的職責），確保壞檔案會得到清楚的錯誤而不是讓 app 崩潰。
 */
export function deserializeProject(json: string): BprojFile {
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch (error) {
    throw new BprojParseError(`無法解析 .bproj 檔：不是合法的 JSON（${(error as Error).message}）`);
  }

  if (!isPlainObject(raw)) {
    throw new BprojParseError('無法解析 .bproj 檔：頂層必須是物件');
  }
  if (raw.formatVersion !== BPROJ_FORMAT_VERSION) {
    throw new BprojParseError(
      `不支援的 .bproj 版本：${String(raw.formatVersion)}（目前支援 ${BPROJ_FORMAT_VERSION}）`,
    );
  }
  if (!isPlainObject(raw.project)) {
    throw new BprojParseError('無法解析 .bproj 檔：缺少 project 欄位');
  }
  const candidate = raw.project as unknown as Project;
  if (typeof candidate.id !== 'string' || !Array.isArray(candidate.tasks)) {
    throw new BprojParseError('無法解析 .bproj 檔：project 結構不完整');
  }
  // `ignoreResourceCalendar` 是 format v1 的向後相容新增欄位。舊檔沒有它時
  // 必須在進 store 前補成明確的 false，不能讓 undefined 污染 core domain。
  const project: Project = {
    ...candidate,
    customFields: Array.isArray(candidate.customFields) ? candidate.customFields : [],
    // Budget resources were added without a format-version bump. Legacy v1
    // files therefore need an explicit empty collection before they enter the
    // store, so every later save round-trips the current project contract.
    budgetAssignments: Array.isArray(candidate.budgetAssignments) ? candidate.budgetAssignments : [],
    tasks: candidate.tasks.map((task) => ({
      ...task,
      ignoreResourceCalendar:
        typeof task.ignoreResourceCalendar === 'boolean' ? task.ignoreResourceCalendar : false,
    })),
  };

  const { view, unknownViewFields } = parseViewSnapshot(raw.view);
  const savedAt = typeof raw.savedAt === 'string' ? raw.savedAt : new Date().toISOString();
  const unknownTopLevelFields = extractUnknown(raw, KNOWN_TOP_LEVEL_KEYS);

  return {
    formatVersion: BPROJ_FORMAT_VERSION,
    savedAt,
    project,
    view,
    unknownTopLevelFields,
    unknownViewFields,
  };
}
